import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from './api/client';

/** Маршрутизация по hash: '#/requests', '#/requests/<id>', ... */
export function useHashRoute(): string {
  const [hash, setHash] = useState(() => window.location.hash || '#/requests');
  useEffect(() => {
    const onChange = () => setHash(window.location.hash || '#/requests');
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return hash.replace(/^#/, '') || '/requests';
}

export function navigate(path: string) {
  window.location.hash = path.startsWith('#') ? path : `#${path}`;
}

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: ApiError | Error | null;
  reload: () => void;
}

/** Загрузка данных с состояниями loading/error/reload. */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const seq = useRef(0);

  const run = useCallback(() => {
    const my = ++seq.current;
    setLoading(true);
    setError(null);
    fn()
      .then((d) => { if (seq.current === my) { setData(d); setLoading(false); } })
      .catch((e) => {
        if (seq.current === my) {
          setError(e instanceof Error ? e : new Error(String(e)));
          setLoading(false);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => { run(); }, [run]);

  return { data, loading, error, reload: run };
}

/** Объединённая загрузка нескольких источников. */
export function useAsyncAll<T extends Record<string, () => Promise<unknown>>>(
  sources: T,
  deps: unknown[] = [],
) {
  type Out = { [K in keyof T]: Awaited<ReturnType<T[K]>> };
  return useAsync<Out>(async () => {
    const keys = Object.keys(sources) as (keyof T)[];
    const values = await Promise.all(keys.map((k) => sources[k]().catch((e) => e)));
    const out: Record<string, unknown> = {};
    keys.forEach((k, i) => { out[k as string] = values[i]; });
    return out as Out;
  }, deps);
}
